import type { AuditEntry } from '@/bindings/AuditEntry'
import type { Language } from '@/bindings/Language'
import type { LocalizedText } from '@/bindings/LocalizedText'
import { tableKey } from '@/features/cleanup/api'
import { parseOrderStatus } from '@/features/ordering/order-rules'
import { LANGUAGE_NAMES, isLanguage } from '@/lib/i18n/languages'
import { resolveLocalizedText } from '@/lib/i18n/localized-text'
import type { MessageKey } from '@/lib/i18n/messages/en'
import { en } from '@/lib/i18n/messages/en'
import type { Translate } from '@/lib/i18n/translate'
import { STEP_LABELS, localeOf } from '@/lib/menu-format'
import { ROLE_LABELS, isRole } from '@/lib/roles'
import { formatDate, formatDateTime } from '@/lib/time'

export interface Change {
  field: string
  label: string
  from: string
  to: string
}

export interface RenderedEntry {
  text: string
  changes: Change[]
}

type Params = Record<string, unknown>

const ORDERING_MODES = ['open', 'login', 'closed'] as const
const PIN_WORDS = ['set', 'unset', 'changed'] as const
const NUMERIC_FIELDS = new Set(['price', 'line_total', 'quantity', 'returned_quantity', 'stock', 'open_order_expiry_minutes', 'active_order_cap'])

function isMessageKey(key: string): key is MessageKey {
  return key in en
}

function isLocalizedText(value: unknown): value is LocalizedText {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  return Object.entries(value).every(([key, text]) => isLanguage(key) && typeof text === 'string')
}

function shortId(value: string): string {
  return value.length > 8 ? value.slice(0, 8) : value
}

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/** Opening hours as one line per day: "Monday 08:00 to 22:00; Tuesday closed". */
function formatOpeningHours(value: object, t: Translate): string {
  const days = (value as { days?: Record<string, { closed?: boolean; open?: string; close?: string }> }).days ?? {}
  return WEEKDAYS.filter((day) => day in days)
    .map((day) => {
      const hours = days[day]!
      const range = hours.closed || !hours.open || !hours.close ? t('openingHours.closed') : `${hours.open} ${t('openingHours.to')} ${hours.close}`
      return `${t(`day.${day}`)} ${range}`
    })
    .join('; ')
}

/**
 * One param or change value as the reader should see it: a name in the
 * viewer's language, a role or status by its label, a number in the locale,
 * a secret only as "set" / "unset". `field` picks the special cases.
 */
export function formatValue(value: unknown, field: string, t: Translate, language: Language): string {
  if (value === null || value === undefined || value === '') return t('audit.none')
  if (typeof value === 'boolean') return t(value ? 'audit.yes' : 'audit.no')
  if (Array.isArray(value)) return value.length === 0 ? t('audit.none') : value.map((item) => formatValue(item, field, t, language)).join(', ')
  if (isLocalizedText(value)) return resolveLocalizedText(value, language) ?? t('audit.none')
  if (typeof value === 'number') return value.toLocaleString(localeOf(language))
  if (field === 'opening_hours' && typeof value === 'object') return formatOpeningHours(value, t)
  if (typeof value !== 'string') return JSON.stringify(value)
  switch (field) {
    case 'role':
      return isRole(value) ? t(ROLE_LABELS[value]) : value
    case 'from':
    case 'to': {
      const status = parseOrderStatus(value)
      return status ? t(`track.status.${status}`) : value
    }
    case 'ordering_mode':
      return (ORDERING_MODES as readonly string[]).includes(value) ? t(`audit.mode.${value as (typeof ORDERING_MODES)[number]}`) : value
    case 'order_pin':
      return (PIN_WORDS as readonly string[]).includes(value) ? t(`audit.pin.${value as (typeof PIN_WORDS)[number]}`) : value
    case 'step':
      return value in STEP_LABELS ? t(STEP_LABELS[value as keyof typeof STEP_LABELS]) : value
    case 'main_language':
    case 'secondary_languages':
      return isLanguage(value) ? LANGUAGE_NAMES[value] : value
    case 'image_id':
    case 'category_id':
      return shortId(value)
    case 'before':
      return formatDateTime(value, language)
    case 'hold_until':
      return formatDate(value, language)
    default:
      if (NUMERIC_FIELDS.has(field) && !Number.isNaN(Number(value))) return Number(value).toLocaleString(localeOf(language))
      return value
  }
}

function fieldLabel(field: string, t: Translate): string {
  const key = `audit.field.${field}`
  return isMessageKey(key) ? t(key) : field
}

function changesOf(params: Params, t: Translate, language: Language): Change[] {
  const changes = params.changes
  if (typeof changes !== 'object' || changes === null || Array.isArray(changes)) return []
  return Object.entries(changes).map(([field, pair]) => {
    const { from, to } = (typeof pair === 'object' && pair !== null ? pair : {}) as { from?: unknown; to?: unknown }
    return { field, label: fieldLabel(field, t), from: formatValue(from, field, t, language), to: formatValue(to, field, t, language) }
  })
}

/** The rows of `cleanup.ran` and `restaurant.deleted`: "Orders: 120, Order lines: 400". */
function cleanupRows(rows: unknown, t: Translate, language: Language): string {
  if (typeof rows !== 'object' || rows === null) return t('audit.none')
  return Object.entries(rows)
    .map(([table, count]) => {
      const key = tableKey(table)
      return `${key ? t(key) : table}: ${formatValue(count, 'rows', t, language)}`
    })
    .join(', ')
}

/**
 * Key + params → the headline sentence and the changes table (T4.8). An
 * unknown key still shows, by name, so a new producer is never silent.
 */
export function renderAuditEntry({ action, params }: AuditEntry, t: Translate, language: Language): RenderedEntry {
  const f = (field: string) => formatValue(params[field], field, t, language)
  const account = () => (params.account_name ? f('account_name') : t('audit.actor.unknown'))
  // The one thing the row is about, whichever snapshot the producer wrote.
  const name = () => f(['item_name', 'category_name', 'restaurant_name'].find((field) => params[field] != null) ?? 'item_name')
  const changes = changesOf(params, t, language)
  const key = `audit.action.${action}`
  if (!isMessageKey(key)) return { text: t('audit.unknown', { key: action }), changes }
  let text: string
  switch (action) {
    case 'restaurant.superuser_changed':
      text = params.from_account_id
        ? t('audit.action.restaurant.superuser_replaced', { account: account(), from: params.from_account_name ? f('from_account_name') : t('audit.actor.unknown') })
        : t(key, { account: account() })
      break
    case 'restaurant.notice_sent': {
      const template = `notice.template.${String(params.template)}`
      text = t(key, { template: isMessageKey(template) ? t(template) : String(params.template), name: name(), recipients: f('recipients') })
      break
    }
    case 'restaurant.deleted':
      text = t(key, { name: name(), slug: f('slug'), rows: cleanupRows(params.rows, t, language) })
      break
    case 'cleanup.ran': {
      const group = `cleanup.group.${String(params.group)}`
      text = t(key, { group: isMessageKey(group) ? t(group) : String(params.group), before: f('before'), rows: cleanupRows(params.rows, t, language) })
      break
    }
    default:
      text = t(key, {
        name: name(),
        names: f('categories'),
        branch: f('branch_name'),
        table: f('table_name'),
        fromTable: f('from_table_name'),
        account: account(),
        role: f('role'),
        order: f('order_number'),
        code: f('session_code'),
        into: f('into_session_code'),
        from: f('from'),
        to: f('to'),
        price: f('price'),
        email: f('email'),
      })
  }
  return { text, changes }
}
